import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { v4 as uuidv4 } from 'uuid';
import { getCurrentDate } from '../utils/dateUtils';
import logo from '../Images/logo.png';

// Generate invoice PDF and return receipt details
export const generateInvoicePDF = (student, paymentData) => {
  return new Promise((resolve) => {
    const doc = new jsPDF();
    const receiptNumber = `117-${uuidv4().replace(/\D/g, '').slice(0, 7)}`;
    const img = new Image();
    img.src = logo;

    img.onload = () => {
      doc.setFont('Times', 'bold');

      // Title: INVOICE RECEIPT
      doc.setFontSize(28);
      doc.text('INVOICE/RECEIPT', 105, 25, { align: 'center' });

      // Receipt Information
      doc.setFontSize(12);
      doc.setFont('Times', 'normal');
      doc.text(`Receipt number: ${receiptNumber}`, 15, 40);
      doc.text(`Date: ${getCurrentDate()}`, 15, 50);

      // Add Logo
      const logoWidth = 10, logoHeight = 10;
      doc.addImage(img, 'PNG', 15, 60, logoWidth, logoHeight);

      // Company Information
      doc.setFont('Times', 'bold');
      doc.text('Pumo Technovation', 30, 65);
      doc.setFont('Times', 'normal');
      doc.text(`${student.adminbranch || 'N/A'}`, 30, 75);
      doc.text(`${student.adminlocation || 'N/A'}`, 30, 80);
      doc.text('info@pumotechnovation.com', 30, 85);

      // Bill To Section
      doc.setFont('Times', 'bold');
      doc.text('Bill To', 140, 65);
      doc.setFont('Times', 'normal');
      doc.text(`${student.name || 'N/A'}`, 140, 75);
      doc.text(`${student.contactNo || 'N/A'}`, 140, 80);

      // Handle Multi-Line Address
      const address = student.address || 'N/A';
      const addressLines = doc.splitTextToSize(address, 60);
      doc.text(`Address: ${addressLines[0]}`, 140, 85);
      if (addressLines.length > 1) {
        for (let i = 1; i < addressLines.length; i++) {
          doc.text(addressLines[i], 140, 85 + (i * 5));
        }
      }

      // Calculate payment details
      const paymentAmount = parseFloat(paymentData.paymentAmount) || 0;
      let cgstAmount = 0, sgstAmount = 0, finalAmount = paymentAmount;

      // Add tax calculations for non-Cash payments
      if (paymentData.modeOfPayment !== 'Cash') {
        cgstAmount = paymentAmount * 0.09;
        sgstAmount = paymentAmount * 0.09;
        finalAmount = paymentAmount - cgstAmount - sgstAmount;
      }

      // Prepare table rows based on payment mode
      const tableRows = [
        ["Course", student.course || 'N/A'],
        ["Mode of Payment", paymentData.modeOfPayment || 'N/A']
      ];

      // Add additional details for non-Cash payments
      if (paymentData.modeOfPayment !== 'Cash') {
        tableRows.push(
          ["Reference No", paymentData.referenceNo || 'N/A'],
          ["Amount", `${finalAmount.toFixed(2)}`],
          ["CGST (9%)", `${cgstAmount.toFixed(2)}`],
          ["SGST (9%)", `${sgstAmount.toFixed(2)}`]
        );
      }

      // Calculate how payment affects pendingFees up to pendingFees4
      const currentPendingFees = parseFloat(student.pendingFees) || 0;
      const currentPendingFees2 = parseFloat(student.pendingFees2) || 0;
      const currentPendingFees3 = parseFloat(student.pendingFees3) || 0;
      const currentPendingFees4 = parseFloat(student.pendingFees4) || 0;
      
      let newPendingFees = currentPendingFees;
      let newPendingFees2 = currentPendingFees2;
      let newPendingFees3 = currentPendingFees3;
      let newPendingFees4 = currentPendingFees4;
      let remainingPayment = paymentAmount;

      // First reduce pendingFees
      if (remainingPayment >= currentPendingFees) {
        remainingPayment -= currentPendingFees;
        newPendingFees = 0;
        
        // If there's remaining payment and pendingFees2 exists, reduce it
        if (remainingPayment > 0 && currentPendingFees2 > 0) {
          if (remainingPayment >= currentPendingFees2) {
            remainingPayment -= currentPendingFees2;
            newPendingFees2 = 0;
            
            // If there's remaining payment and pendingFees3 exists, reduce it
            if (remainingPayment > 0 && currentPendingFees3 > 0) {
              if (remainingPayment >= currentPendingFees3) {
                remainingPayment -= currentPendingFees3;
                newPendingFees3 = 0;
                
                // If there's remaining payment and pendingFees4 exists, reduce it
                if (remainingPayment > 0 && currentPendingFees4 > 0) {
                  newPendingFees4 = Math.max(0, currentPendingFees4 - remainingPayment);
                }
              } else {
                // Payment partially covers pendingFees3
                newPendingFees3 = currentPendingFees3 - remainingPayment;
                // pendingFees4 remains unchanged
              }
            }
          } else {
            // Payment partially covers pendingFees2
            newPendingFees2 = currentPendingFees2 - remainingPayment;
            // pendingFees3 and pendingFees4 remain unchanged
          }
        }
      } else {
        // Payment is less than pendingFees
        newPendingFees = currentPendingFees - remainingPayment;
        // pendingFees2, pendingFees3, and pendingFees4 remain unchanged
      }

      // Add payment amount and remaining pending fees
      tableRows.push(["Payment Amount", `${paymentAmount.toFixed(2)}`]);
      
      // Add Pending Fees information
      tableRows.push(["Pending Fees", `${newPendingFees.toFixed(2)}`]);
      if (newPendingFees > 0) {
        tableRows.push(["Pending Fees Date", paymentData.pendingFeesDate || 'N/A']);
      }
      
      // Add Pending Fees 2 information if it exists and is not zero
      if (currentPendingFees2 > 0) {
        tableRows.push(["Pending Fees 2", `${newPendingFees2.toFixed(2)}`]);
        if (newPendingFees2 > 0) {
          tableRows.push(["Pending Fees Date 2", paymentData.pendingFeesDate2 || 'N/A']);
        }
      }
      
      // Add Pending Fees 3 information if it exists and is not zero
      if (currentPendingFees3 > 0) {
        tableRows.push(["Pending Fees 3", `${newPendingFees3.toFixed(2)}`]);
        if (newPendingFees3 > 0) {
          tableRows.push(["Pending Fees Date 3", paymentData.pendingFeesDate3 || 'N/A']);
        }
      }
      
      // Add Pending Fees 4 information if it exists and is not zero
      if (currentPendingFees4 > 0) {
        tableRows.push(["Pending Fees 4", `${newPendingFees4.toFixed(2)}`]);
        if (newPendingFees4 > 0) {
          tableRows.push(["Pending Fees Date 4", paymentData.pendingFeesDate4 || 'N/A']);
        }
      }

      // Generate Table
      doc.autoTable({
        head: [["Description", "Details"]],
        body: tableRows,
        startY: 120,
        theme: 'grid',
        styles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'normal',
          fontSize: 12,
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          font: 'times'
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          font: 'times'
        },
        bodyStyles: {
          fillColor: false,
          textColor: [0, 0, 0]
        }
      });

      const finalY = doc.lastAutoTable.finalY + 20;
      doc.setFont('Times', 'italic');
      doc.text("*****This invoice is generated automatically*****", 105, finalY, { align: 'center' });

      // Save PDF
      doc.save(`invoice-receipt-${student.name}.pdf`);

      // Resolve with receipt details
      resolve({
        receiptNumber,
        cgstAmount: paymentData.modeOfPayment === 'Cash' ? 0 : cgstAmount,
        sgstAmount: paymentData.modeOfPayment === 'Cash' ? 0 : sgstAmount,
        finalAmount: paymentData.modeOfPayment === 'Cash' ? paymentAmount : finalAmount
      });
    };
  });
};

// Regenerate an invoice PDF from existing invoice data

export const regenerateInvoicePDF = (invoice, student) => {
  return new Promise((resolve) => {
    const doc = new jsPDF();
    const receiptNumber = invoice.receipt_no;

    const img = new Image();
    img.src = logo;
    img.onload = () => {
      doc.setFont('Times', 'bold');

      // Title: INVOICE RECEIPT
      doc.setFontSize(28);
      doc.text('INVOICE/RECEIPT', 105, 25, { align: 'center' });

      // Receipt Information
      doc.setFontSize(12);
      doc.setFont('Times', 'normal');
      doc.text(`Receipt number: ${receiptNumber}`, 15, 40);
      const dateOnly = invoice.paymentDate ? invoice.paymentDate.split('T')[0] : 'N/A';
      doc.text(`Date: ${dateOnly}`, 15, 50);
      
      // Add Logo
      const logoWidth = 10, logoHeight = 10;
      doc.addImage(img, 'PNG', 15, 60, logoWidth, logoHeight);

      // Company Information
      doc.setFont('Times', 'bold');
      doc.text('Pumo Technovation', 30, 65);
      doc.setFont('Times', 'normal');
      doc.text(`${student.adminbranch || 'N/A'}`, 30, 75);
      doc.text(`${student.adminlocation || 'N/A'}`, 30, 80);
      doc.text('info@pumotechnovation.com', 30, 85);

      // Bill To Section
      doc.setFont('Times', 'bold');
      doc.text('Bill To', 140, 65);
      doc.setFont('Times', 'normal');
      doc.text(`${student.name || 'N/A'}`, 140, 75);
      doc.text(`${student.contactNo || 'N/A'}`, 140, 80);

      // Handle Multi-Line Address properly
      const address = student.address || 'N/A';
      // First, write the "Address:" label
      doc.text("Address:", 140, 85);
      
      // Then split and write the address content with proper indentation
      const addressContent = doc.splitTextToSize(address, 40); // More reduced width to ensure it fits
      
      // Check if address is very long and handle accordingly
      if (addressContent.length > 1) {
        // For multi-line addresses, create a more compact display
        for (let i = 0; i < Math.min(addressContent.length, 3); i++) { // Limit to 3 lines max
          doc.text(addressContent[i], 140, 90 + (i * 4)); // Reduced vertical spacing
        }
        
        // If there are more lines, add ellipsis or further compression
        if (addressContent.length > 3) {
          const remainingText = addressContent.slice(3).join(" ");
          const truncatedText = remainingText.length > 20 ? 
                              remainingText.substring(0, 20) + "..." : 
                              remainingText;
          doc.text(truncatedText, 140, 102);
        }
      } else {
        // Single line address
        doc.text(addressContent[0], 140, 90);
      }

      // Calculate payment details
      const paymentAmount = parseFloat(invoice.paidAmount) || 0;
      const modeOfPayment = invoice.registrationPaymentMode || 'N/A';
      const cgstAmount = parseFloat(invoice.cgst) || 0;
      const sgstAmount = parseFloat(invoice.sgst) || 0;
      const finalAmount = parseFloat(invoice.amount) || 0;

      // Prepare table rows based on payment mode
      const tableRows = [
        ["Course", student.course || 'N/A'],
        ["Mode of Payment", modeOfPayment]
      ];

      // Add additional details for non-Cash payments
      if (modeOfPayment !== 'Cash') {
        tableRows.push(
          ["Reference No", invoice.registrationReferenceNo || 'N/A'],
          ["Amount", `${finalAmount.toFixed(2)}`],
          ["CGST (9%)", `${cgstAmount.toFixed(2)}`],
          ["SGST (9%)", `${sgstAmount.toFixed(2)}`]
        );
      }

      // Add payment amount
      tableRows.push(["Payment Amount", `${paymentAmount.toFixed(2)}`]);
      
      // Add Pending Fees information if available
      const pendingFees = parseFloat(student.pendingFees) || 0;
      if (pendingFees > 0) {
        tableRows.push(
          ["Pending Fees", `${pendingFees.toFixed(2)}`],
          ["Pending Fees Date", student.pendingFeesDate || 'N/A']
        );
      }
      
      // Add Pending Fees 2 information if it exists and is not zero
      const pendingFees2 = parseFloat(student.pendingFees2) || 0;
      if (pendingFees2 > 0) {
        tableRows.push(
          ["Pending Fees 2", `${pendingFees2.toFixed(2)}`],
          ["Pending Fees Date 2", student.pendingFeesDate2 || 'N/A']
        );
      }
      
      // Add Pending Fees 3 information if it exists and is not zero
      const pendingFees3 = parseFloat(student.pendingFees3) || 0;
      if (pendingFees3 > 0) {
        tableRows.push(
          ["Pending Fees 3", `${pendingFees3.toFixed(2)}`],
          ["Pending Fees Date 3", student.pendingFeesDate3 || 'N/A']
        );
      }
      
      // Add Pending Fees 4 information if it exists and is not zero
      const pendingFees4 = parseFloat(student.pendingFees4) || 0;
      if (pendingFees4 > 0) {
        tableRows.push(
          ["Pending Fees 4", `${pendingFees4.toFixed(2)}`],
          ["Pending Fees Date 4", student.pendingFeesDate4 || 'N/A']
        );
      }

      // Add installment information if available
      if (invoice.paymentInstallment) {
        tableRows.push(["Installment", invoice.paymentInstallment || 'N/A']);
      }

      // Generate Table - ensure it starts with enough spacing after the address
      // Calculate the start Y position based on address length
      const addressLineCount = addressContent.length;
      const tableStartY = Math.max(120, 90 + (addressLineCount * 5));
      
      doc.autoTable({
        head: [["Description", "Details"]],
        body: tableRows,
        startY: tableStartY,
        theme: 'grid',
        styles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'normal',
          fontSize: 12,
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          font: 'times'
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          font: 'times'
        },
        bodyStyles: {
          fillColor: false,
          textColor: [0, 0, 0]
        }
      });

      const finalY = doc.lastAutoTable.finalY + 20;
      doc.setFont('Times', 'italic');
      doc.text("*****This invoice is generated automatically*****", 105, finalY, { align: 'center' });

      // Save PDF with invoice installment in the filename
      const installment = invoice.paymentInstallment || 'single';
      doc.save(`invoice-receipt-${student.name}-installment-${installment}.pdf`);
      
      resolve({
        receiptNumber,
        cgstAmount,
        sgstAmount,
        finalAmount
      });
    };
  });
};